/**
 * État de lecture d'un ticket par le demandeur.
 *
 * Modèle : le champ `requesterLastReadAt` (Timestamp) sur le document helpTickets
 * est mis à jour quand le demandeur (ticket.userId) ouvre le détail du ticket.
 * Une "réponse" est un message dont l'auteur n'est pas le demandeur.
 */

export const READ_STATES = {
  NO_REPLY: "no_reply",
  READ: "read",
  UNREAD: "unread",
};

export const toMillisTs = (timestamp) => {
  if (!timestamp) return 0;
  if (timestamp.toMillis) return timestamp.toMillis();
  if (timestamp.seconds) return timestamp.seconds * 1000;
  const d = new Date(timestamp);
  return isNaN(d.getTime()) ? 0 : d.getTime();
};

/**
 * Dernier message adressé au demandeur (auteur ≠ demandeur).
 * @param {Object} ticket
 * @returns {Object|null}
 */
export const getLastReplyToRequester = (ticket) => {
  if (!ticket || !Array.isArray(ticket.messages)) return null;
  const replies = ticket.messages.filter(
    (msg) => msg.authorId !== ticket.userId
  );
  if (replies.length === 0) return null;
  return replies.reduce((latest, msg) =>
    toMillisTs(msg.createdAt) > toMillisTs(latest.createdAt) ? msg : latest
  );
};

/**
 * État de lecture du ticket par le demandeur.
 * @param {Object} ticket
 * @returns {string} READ_STATES.NO_REPLY | READ | UNREAD
 */
export const getRequesterReadState = (ticket) => {
  const lastReply = getLastReplyToRequester(ticket);
  if (!lastReply) return READ_STATES.NO_REPLY;
  return toMillisTs(ticket.requesterLastReadAt) >= toMillisTs(lastReply.createdAt)
    ? READ_STATES.READ
    : READ_STATES.UNREAD;
};

/**
 * Le ticket porte-t-il une réponse non lue POUR cet utilisateur ?
 * Vrai uniquement s'il en est le demandeur : l'état de lecture est stocké
 * par ticket (requesterLastReadAt), il ne veut rien dire pour les collègues
 * qui voient le ticket dans la liste de leur établissement.
 * @param {Object} ticket
 * @param {string} userId
 * @returns {boolean}
 */
export const hasUnreadReplyForUser = (ticket, userId) => {
  if (!ticket || !userId || ticket.userId !== userId) return false;
  return getRequesterReadState(ticket) === READ_STATES.UNREAD;
};

/**
 * Nombre de tickets dont l'utilisateur est le demandeur et qui portent une
 * réponse non lue.
 * @param {Array} tickets
 * @param {string} userId
 * @returns {number}
 */
export const countUnreadRepliesForUser = (tickets, userId) =>
  (tickets || []).filter((ticket) => hasUnreadReplyForUser(ticket, userId)).length;
